import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useMemo, useState } from "react";
import CustomButton from "./Button";
import Loader from "./Loader";
import { useToast } from "./Toast";
import apiService from "../Services/api.service";
import { UI, btnSx } from "./ActionDrawer";

/* CustomButton in the dashboard button language. CustomButton spreads props
   after its own sx, so ours wins. Behaviour is untouched. */
const Btn = ({ variant = "solid", sx, ...props }) => (
  <CustomButton {...props} sx={{ ...btnSx(variant, props.disabled), ...sx }} />
);

const rupee = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const TEMPLATES = [
  { value: "standard_50_50", label: "Standard 50/50", advancePct: 50 },
  { value: "30_70_dispatch", label: "30/70 before dispatch", advancePct: 30 },
  { value: "100_advance", label: "100% advance", advancePct: 100 },
  { value: "custom", label: "Custom", advancePct: null },
];

/* Pill used for the payment template choices. */
const pillSx = (on) => ({
  textTransform: "none",
  boxShadow: "none",
  minWidth: 0,
  borderRadius: "14px",
  padding: "4px 13px",
  fontSize: 13,
  fontWeight: on ? 500 : 400,
  fontFamily: "inherit",
  border: "1px solid",
  borderColor: on ? UI.p : UI.line,
  bgcolor: on ? UI.p : UI.surface,
  color: on ? UI.surface : UI.ink2,
  "&:hover": {
    boxShadow: "none",
    bgcolor: on ? UI.pDark : UI.p50,
    borderColor: UI.p,
  },
});

const PillGroup = ({ options, value, onChange }) => (
  <Box sx={{ display: "flex", gap: "7px", flexWrap: "wrap" }}>
    {options.map((opt) => (
      <Button
        key={opt.value}
        size="small"
        variant={value === opt.value ? "contained" : "outlined"}
        disableElevation
        onClick={() => onChange(opt.value)}
        sx={pillSx(value === opt.value)}
      >
        {opt.label}
      </Button>
    ))}
  </Box>
);

const ReviseEmiTermsModal = ({ open, orderId, onClose, onSaved }) => {
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);

  const [template, setTemplate] = useState("standard_50_50");
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [balanceAmount, setBalanceAmount] = useState("");
  const [explanation, setExplanation] = useState(
    "The EMI application was not approved. We can proceed on standard terms.",
  );

  useEffect(() => {
    if (!open || !orderId) return;
    setLoading(true);
    apiService
      .getOrder(orderId, token)
      .then((res) => {
        setOrder(res.data);
        const total = Number(res.data.quote?.totalAmount || 0);
        const half = Math.round(total / 2);
        setAdvanceAmount(half);
        setBalanceAmount(total - half);
        setTemplate("standard_50_50");
      })
      .catch(() => {
        toast.error("Failed to load order");
        onClose();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId]);

  const totalAmount = Number(order?.quote?.totalAmount || 0);

  const handleTemplateChange = (value) => {
    setTemplate(value);
    const meta = TEMPLATES.find((t) => t.value === value);
    if (!meta || meta.advancePct === null) return; // custom — leave fields editable
    const advance = Math.round((totalAmount * meta.advancePct) / 100);
    setAdvanceAmount(advance);
    setBalanceAmount(totalAmount - advance);
  };

  const isCustom = template === "custom";

  const handleAdvanceChange = (value) => {
    setAdvanceAmount(value);
    if (isCustom) {
      setBalanceAmount(Math.max(totalAmount - Number(value || 0), 0));
    }
  };

  const handleSubmit = async () => {
    if (advanceAmount === "" || balanceAmount === "") {
      toast.error("Advance amount and balance are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiService.reviseEmiPaymentTerms(
        orderId,
        {
          template,
          advanceAmount: Number(advanceAmount),
          balanceAmount: Number(balanceAmount),
          explanation,
        },
        token,
      );
      toast.success("Revised payment terms created");
      onSaved(res.data);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to create revised terms",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: UI.surface,
          backgroundImage: "none",
          border: "1px solid",
          borderColor: UI.line,
          borderRadius: "10px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "10px",
          padding: "16px 18px",
          borderBottom: "1px solid",
          borderColor: UI.line,
        }}
      >
        <Box>
          <Box sx={{ fontSize: 16, fontWeight: 500, color: UI.ink }}>
            Create revised payment terms
          </Box>
          {order && (
            <Box sx={{ fontSize: 12, color: UI.ink3, marginTop: "2px" }}>
              {order.clinic?.name}
            </Box>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: "18px" }}>
        {loading || !order ? (
          <Box sx={{ py: 4 }}>
            <Loader />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                padding: "10px 13px",
                borderRadius: "4px",
                bgcolor: UI.redBg,
                border: "1px solid",
                borderColor: UI.red,
                color: UI.redInk,
                fontSize: 12,
                marginBottom: "16px",
              }}
            >
              Massfintech rejected this EMI case
              {order.emi?.rejectionReason
                ? `. Reason: ${order.emi.rejectionReason}`
                : "."}
            </Box>

            <Box
              sx={{
                bgcolor: UI.bg2,
                border: "1px solid",
                borderColor: UI.line,
                borderRadius: "6px",
                padding: "10px 12px",
                marginBottom: "18px",
              }}
            >
              <Box sx={{ fontSize: 11, color: UI.ink3 }}>Original terms</Box>
              <Box
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: UI.ink,
                  marginTop: "2px",
                }}
              >
                {order.quote?.paymentTerm || "—"}
              </Box>
            </Box>

            <Box
              sx={{
                fontSize: 12,
                fontWeight: 500,
                letterSpacing: ".2px",
                color: UI.ink2,
                marginBottom: "10px",
              }}
            >
              New payment template
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <PillGroup
                options={TEMPLATES}
                value={template}
                onChange={handleTemplateChange}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                mb: 2.5,
              }}
            >
              <TextField
                label="Advance amount"
                type="number"
                size="small"
                fullWidth
                value={advanceAmount}
                disabled={!isCustom}
                onChange={(e) => handleAdvanceChange(e.target.value)}
              />
              <TextField
                label="Balance on dispatch"
                type="number"
                size="small"
                fullWidth
                value={balanceAmount}
                disabled={!isCustom}
                onChange={(e) => setBalanceAmount(e.target.value)}
              />
            </Box>

            <TextField
              label="Explanation to the customer"
              multiline
              minRows={3}
              size="small"
              fullWidth
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              sx={{ mb: 2.5 }}
            />

            <Box
              sx={{
                padding: "10px 13px",
                borderRadius: "4px",
                bgcolor: UI.amberBg,
                border: "1px solid",
                borderColor: UI.amberLine,
                color: UI.amberInk,
                fontSize: 12,
                marginBottom: "4px",
              }}
            >
              Open decision. If the customer declines revised terms, the order
              has no terminal state.
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column-reverse", sm: "row" },
                justifyContent: "flex-end",
                gap: "8px",
                marginTop: "22px",
                paddingTop: "16px",
                borderTop: "1px solid",
                borderTopColor: UI.line,
              }}
            >
              <Btn
                variant="ghost"
                text="Cancel"
                onClick={onClose}
                width="120px"
                sx={{ width: { xs: "100%", sm: "120px" } }}
              />
              <Btn
                text="Send revised terms"
                onClick={handleSubmit}
                width="200px"
                sx={{ width: { xs: "100%", sm: "200px" } }}
                disabled={submitting}
              />
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReviseEmiTermsModal;
